// ─────────────────────────────────────────────────────────────────────────────
// AiChatDrawerComponent
// ─────────────────────────────────────────────────────────────────────────────
// Fixed right-side drawer shell. Responsibilities:
//  - Slide-in/out animation driven by the [open] input
//  - Backdrop click to close
//  - Header actions to toggle history view and create new conversations
// ─────────────────────────────────────────────────────────────────────────────

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';

import { AppState } from 'src/app/core/app.state';
import { IAiConversation } from '../../models/ai-chat.models';
import { ChatHistoryComponent } from '../chat-history/chat-history.component';
import { ChatWindowComponent } from '../chat-window/chat-window.component';
import * as aiChatActions from '../../store/ai-chat.actions';
import * as aiChatSelectors from '../../store/ai-chat.selectors';
import { UiModule } from 'src/app/ui/ui.module';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-ai-chat-drawer',
  standalone: true,
  imports: [CommonModule, ChatHistoryComponent, ChatWindowComponent, UiModule, TranslateModule],
  templateUrl: './ai-chat-drawer.component.html',
  styleUrls: ['./ai-chat-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatDrawerComponent implements OnInit, OnChanges, OnDestroy {

  // ── Decorated properties ───────────────────────────────────────────────────

  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  // ── Public properties (from store) ────────────────────────────────────────

  activeConversation$!: Observable<IAiConversation | null>;
  activeConversationLoading$!: Observable<boolean>;

  // ── Public properties (local state) ───────────────────────────────────────

  isHistoryOpen = false;

  // ── Private properties ─────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(private store: Store<AppState>) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.activeConversation$ = this.store
      .select(aiChatSelectors.selectActiveConversation)
      .pipe(takeUntil(this.destroy$));

    this.activeConversationLoading$ = this.store
      .select(aiChatSelectors.selectActiveConversationLoading)
      .pipe(takeUntil(this.destroy$));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true && !changes['open'].previousValue) {
      this.isHistoryOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Header actions ─────────────────────────────────────────────────────────

  toggleHistory(): void {
    this.isHistoryOpen = !this.isHistoryOpen;
  }

  createNewConversation(): void {
    this.store.dispatch(aiChatActions.CreateDraftConversation());
    this.isHistoryOpen = false;
  }

  onConversationSelected(id: string): void {
    this.store.dispatch(aiChatActions.SelectConversation({ id }));
    this.isHistoryOpen = false;
  }

  close(): void {
    this.closed.emit();
  }

}
